import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["columnList", "columnSearch", "selectAllCheckbox"]
  static values = { storageKey: String }

  connect() {
    this.hiddenColumns = new Set(this.loadHiddenColumns())
    this.pendingHiddenColumns = new Set(this.hiddenColumns)
    this.syncCheckboxStates()
    this._observeDropdownOpen()
  }

  loadHiddenColumns() {
    try { return JSON.parse(localStorage.getItem(this.storageKeyValue)) || [] } catch { return [] }
  }

  saveHiddenColumns() {
    localStorage.setItem(this.storageKeyValue, JSON.stringify([...this.hiddenColumns]))
  }

  syncCheckboxStates() {
    if (!this.hasColumnListTarget) return
    this.columnListTarget.querySelectorAll('input[type="checkbox"][data-field]').forEach(cb => {
      cb.checked = !this.hiddenColumns.has(cb.dataset.field)
    })
    this.updateSelectAllState()
  }

  _observeDropdownOpen() {
    if (!this.hasColumnListTarget) return
    const popup = this.columnListTarget.closest('[data-dropdown-target="popup"]')
    if (!popup) return
    this._dropdownObserver = new MutationObserver(() => {
      if (!popup.classList.contains('hidden')) {
        this.pendingHiddenColumns = new Set(this.hiddenColumns)
        if (this.hasColumnSearchTarget) this.columnSearchTarget.value = ''
        this._showAllColumnItems()
        this.syncCheckboxStates()
      }
    })
    this._dropdownObserver.observe(popup, { attributes: true, attributeFilter: ['class'] })
  }

  _showAllColumnItems() {
    if (!this.hasColumnListTarget) return
    this.columnListTarget.querySelectorAll('[data-column-item]').forEach(el => el.style.display = '')
  }

  _closeDropdown() {
    if (!this.hasColumnListTarget) return
    const popup = this.columnListTarget.closest('[data-dropdown-target="popup"]')
    if (popup) popup.classList.add('hidden')
  }

  filterColumns(event) {
    const search = event.target.value.toLowerCase()
    this.columnListTarget.querySelectorAll('[data-column-item]').forEach(el => {
      const label = el.querySelector('label')
      el.style.display = (!search || (label && label.textContent.toLowerCase().includes(search))) ? '' : 'none'
    })
    this.updateSelectAllState()
  }

  toggleColumn(event) {
    const field = event.target.dataset.field
    if (event.target.checked) {
      this.pendingHiddenColumns.delete(field)
    } else {
      this.pendingHiddenColumns.add(field)
    }
    this.updateSelectAllState()
  }

  toggleSelectAll(event) {
    const checked = event.target.checked
    this.columnListTarget.querySelectorAll('[data-column-item] input[type="checkbox"]').forEach(cb => {
      const item = cb.closest('[data-column-item]')
      if (item.style.display === 'none') return
      cb.checked = checked
      if (checked) {
        this.pendingHiddenColumns.delete(cb.dataset.field)
      } else {
        this.pendingHiddenColumns.add(cb.dataset.field)
      }
    })
  }

  updateSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget) return
    const visible = [...this.columnListTarget.querySelectorAll('[data-column-item]')]
      .filter(el => el.style.display !== 'none')
    const allChecked = visible.length > 0 && visible.every(el => el.querySelector('input').checked)
    const someChecked = visible.some(el => el.querySelector('input').checked)
    this.selectAllCheckboxTarget.checked = allChecked
    this.selectAllCheckboxTarget.indeterminate = !allChecked && someChecked
  }

  applyColumns() {
    if (this.hasColumnListTarget) {
      this.columnListTarget.querySelectorAll('[data-column-item]').forEach(el => {
        if (el.style.display === 'none') {
          const cb = el.querySelector('input[type="checkbox"][data-field]')
          if (cb) this.pendingHiddenColumns.add(cb.dataset.field)
        }
      })
    }
    this.hiddenColumns = new Set(this.pendingHiddenColumns)
    this.saveHiddenColumns()
    this._closeDropdown()
    this._notifyDataRows()
  }

  resetColumns() {
    this.pendingHiddenColumns = new Set()
    this.hiddenColumns = new Set()
    this.saveHiddenColumns()
    if (this.hasColumnSearchTarget) this.columnSearchTarget.value = ''
    this._showAllColumnItems()
    this.syncCheckboxStates()
    this._closeDropdown()
    this._notifyDataRows()
  }

  _notifyDataRows() {
    const el = document.querySelector('[data-controller~="data-rows"]')
    if (!el) return
    const ctrl = this.application.getControllerForElementAndIdentifier(el, 'data-rows')
    if (ctrl) {
      ctrl.hiddenColumns = new Set(this.hiddenColumns)
      ctrl.updateGridColumns()
    }
  }

  disconnect() {
    if (this._dropdownObserver) this._dropdownObserver.disconnect()
  }
}
